import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import { Modal } from './Modal';

/**
 * Confirmación CORPORATIVA (reemplaza los window.confirm nativos, los feos "localhost dice").
 * Uso: `const confirmar = useConfirm();` … `if (await confirmar({ titulo, mensaje, destructivo })) …`.
 * Devuelve una promesa<boolean>; Escape / click fuera / Cancelar = false, Aceptar = true.
 */
type ConfirmOpts = {
  titulo: string;
  mensaje?: ReactNode;
  textoAceptar?: string;
  textoCancelar?: string;
  destructivo?: boolean;
};
type Estado = ConfirmOpts & { resolver: (v: boolean) => void };

const Ctx = createContext<(opts: ConfirmOpts | string) => Promise<boolean>>(async () => false);
export const useConfirm = () => useContext(Ctx);

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [estado, setEstado] = useState<Estado | null>(null);

  const confirmar = useCallback((opts: ConfirmOpts | string) => {
    const o = typeof opts === 'string' ? { titulo: opts } : opts;
    return new Promise<boolean>((resolver) => setEstado({ ...o, resolver }));
  }, []);

  const cerrar = (v: boolean) => { estado?.resolver(v); setEstado(null); };

  return (
    <Ctx.Provider value={confirmar}>
      {children}
      {estado && (
        <Modal titulo={estado.titulo} onCerrar={() => cerrar(false)}>
          {estado.mensaje && (
            <p className="mb-6 text-sm font-light leading-relaxed text-gris">{estado.mensaje}</p>
          )}
          <div className="flex justify-end gap-2.5">
            <button
              type="button"
              onClick={() => cerrar(false)}
              className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-gris transition-colors hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-azul-profundo"
            >
              {estado.textoCancelar ?? 'Cancelar'}
            </button>
            <button
              type="button"
              autoFocus
              onClick={() => cerrar(true)}
              className={`rounded-lg px-4 py-2 text-sm font-medium text-white transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ${
                estado.destructivo
                  ? 'bg-red-600 hover:bg-red-700 focus-visible:outline-red-600'
                  : 'bg-azul-profundo hover:bg-azul-noche focus-visible:outline-azul-profundo'
              }`}
            >
              {estado.textoAceptar ?? 'Aceptar'}
            </button>
          </div>
        </Modal>
      )}
    </Ctx.Provider>
  );
}
